"use client"

import { Avatar, Text } from "@/elements"
import React, { FC, useMemo } from "react"
import { FragmentType, graphql, useFragment } from "@/gql"
import { TextVariant } from "@/theme"
import clsx from "clsx"
import { AvatarSize } from "@/elements/Avatar/Avatar"
import { LockIcon } from "@/icons/outline"

const AvatarWithNameAndTitleContentFragment = graphql(`
  fragment AvatarWithNameAndTitleContent on User {
    __typename
    id
    avatarInitials
    profile {
      id
      avatar120: avatarUrl(width: 120)
      avatar240: avatarUrl(width: 240)
    }
  }
`)

export type AvatarWithNameAndTitleSize = "sm" | "md" | "lg" | "xl"
export type AvatarWithNameAndTitleSizeVariant = "sm" | "default" | "lg"

interface Props {
  user: FragmentType<typeof AvatarWithNameAndTitleContentFragment> | undefined | null
  title: string | undefined | null
  subtitle: string | undefined | null
  size: AvatarWithNameAndTitleSize
  variant: AvatarWithNameAndTitleSizeVariant
  titleMaxLines?: number
  subtitleMaxLines?: number
  className?: string
  redacted?: boolean
}

export const AvatarWithNameAndTitleContent: FC<Props> = props => {
  const { title, subtitle, size, variant, className, titleMaxLines, subtitleMaxLines, redacted } = props
  const user = useFragment(AvatarWithNameAndTitleContentFragment, props.user)

  const {
    avatarSize,
    imgSrc,
  }: {
    avatarSize: AvatarSize
    imgSrc: string
  } = useMemo(() => {
    switch (size) {
      case "sm":
        // Size 32px
        return { avatarSize: "sm", imgSrc: user?.profile?.avatar120 || "" }
      case "md":
        // Size 40px
        return { avatarSize: "md", imgSrc: user?.profile?.avatar120 || "" }
      case "lg":
        // Size 48px
        return { avatarSize: "lg", imgSrc: user?.profile?.avatar240 || "" }
      case "xl":
        // Size 56px
        return { avatarSize: "3xl", imgSrc: user?.profile?.avatar240 || "" }
    }
  }, [size, user])

  const {
    titleTextVariant,
    subtitleTextVariant,
  }: {
    titleTextVariant: TextVariant
    subtitleTextVariant: TextVariant
  } = useMemo(() => {
    switch (size) {
      case "sm":
        switch (variant) {
          case "sm":
            return { titleTextVariant: "label", subtitleTextVariant: "body3" }
          default:
            return { titleTextVariant: "body1", subtitleTextVariant: "label" }
        }
      case "md":
        switch (variant) {
          case "sm":
            return { titleTextVariant: "h5", subtitleTextVariant: "body1" }
          case "lg":
            return { titleTextVariant: "h6", subtitleTextVariant: "body1" }
          default:
            return { titleTextVariant: "body1", subtitleTextVariant: "label" }
        }
      case "lg":
        switch (variant) {
          case "lg":
            return { titleTextVariant: "h5", subtitleTextVariant: "body1" }
          default:
            return { titleTextVariant: "h6", subtitleTextVariant: "body1" }
        }
      case "xl":
        return { titleTextVariant: "h4", subtitleTextVariant: "h5" }
    }
  }, [size, variant])

  return (
    <div className={clsx("flex w-full items-center", className)}>
      <Avatar initials={user?.avatarInitials} src={imgSrc} size={avatarSize} redacted={redacted} />
      <div className={clsx("ml-3 flex flex-col")}>
        <div
          className={clsx("flex flex-row items-center pr-10", ["lg", "xl"].includes(size) ? "gap-x-2" : "gap-x-[2px]")}>
          <Text variant={titleTextVariant} className={redacted ? "text-fg-2" : ""} maxLines={titleMaxLines}>
            {title}
            {redacted ? <LockIcon className="ml-1 inline size-4 shrink-0 stroke-fg-2 align-middle" /> : null}
          </Text>
        </div>
        {Boolean(subtitle) && (
          <Text variant={subtitleTextVariant} className="text-fg-2" maxLines={subtitleMaxLines}>
            {subtitle}
          </Text>
        )}
      </div>
    </div>
  )
}
